import type { IOnboardingQuestionnaireDocument } from '../../../../../pages/questionnaires/onboarding/types';
import type { OnboardingQuestionnaireState } from '../../types';
import type { IOnboardingQuestionnaireDocumentPayload } from './types';

export function mergeOnboardingQuestionnaireDocumentsPreservingUrls(
  existingDocuments: IOnboardingQuestionnaireDocument[],
  incomingDocuments: IOnboardingQuestionnaireDocument[],
): IOnboardingQuestionnaireDocument[] {
  const existingById = new Map(
    existingDocuments.map((document) => [String(document.id), document] as const),
  );
  const seenIds = new Set<string>();
  const merged: IOnboardingQuestionnaireDocument[] = [];

  for (const document of incomingDocuments) {
    const documentId = String(document.id);
    if (seenIds.has(documentId)) {
      continue;
    }
    seenIds.add(documentId);
    const existing = existingById.get(documentId);
    merged.push({
      ...existing,
      ...document,
      document_url: document.document_url || existing?.document_url,
      document_type: document.document_type ?? existing?.document_type ?? '',
      file_type: document.file_type ?? existing?.file_type ?? '',
      title: document.title ?? existing?.title ?? '',
    });
  }

  // List/GET/upload payloads can omit files (often PDFs); keep rows the client already has.
  for (const document of existingDocuments) {
    const documentId = String(document.id);
    if (seenIds.has(documentId)) {
      continue;
    }
    seenIds.add(documentId);
    merged.push(document);
  }

  return merged;
}

export function upsertOnboardingQuestionnaireDocument(
  state: OnboardingQuestionnaireState,
  document: IOnboardingQuestionnaireDocument,
) {
  const details = state.onboardingQuestionnaireDetails;
  if (!details || details.id !== document.onboarding_questionnaire_id) {
    return;
  }

  const existingIndex = details.documents.findIndex(
    (existingDocument) => existingDocument.id === document.id,
  );

  if (existingIndex >= 0) {
    details.documents[existingIndex] = document;
  }
  // If the doc isn't in state (e.g., deleted between request and response),
  // don't resurrect it.
}

export function buildOnboardingQuestionnaireDocumentFormData(
  data: IOnboardingQuestionnaireDocumentPayload,
): FormData {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('title', data.title);
  formData.append('document_type', data.document_type);
  if (data.document_sub_type != null) {
    formData.append('document_sub_type', data.document_sub_type);
  }
  return formData;
}
